import { StarsButton as Button } from '@ps/web-shared-components';
import { config } from '@react-spring/web';
import classNames from 'clsx';
import { clamp } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { interval } from 'rxjs';

import Arrow from '@swf/core/assets/icons/arrow.svg';

import styles from './Carousel3D.scss';
import SpringSlide from './components/SpringSlide';

export interface Props {
  children: JSX.Element[];
  sideSlides?: number;
  animationStyle?: keyof typeof config;
  startIndex?: number;
  autoPlay?: boolean;
  arrows?: boolean;
  dots?: boolean;
  className?: string;
}

const mod = (a: number, b: number): number => ((a % b) + b) % b;

const clampOffsetRadius = (offsetRadius: number, slidesLength: number): number =>
  clamp(offsetRadius, 0, Math.floor((slidesLength - 1) / 2));

const modBySlidesLength = (index: number, slidesLength: number): number => mod(index, slidesLength);

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

const slideTrigger = (delta: number, onComplete: (left: boolean) => void) => {
  let x: number;
  let completed = false;
  return (event: React.TouchEvent<HTMLDivElement>) => {
    if (completed) return;
    if (x === undefined) {
      x = event.touches[0].clientX;
    }
    const diff = event.touches[0].clientX - x;

    if (Math.abs(diff) > delta) {
      completed = true;
      onComplete(diff > 1);
    }
  };
};

// adapted from https://github.com/gutiguy/react-spring-3d-carousel

const Carousel3D: React.FC<Props> = ({
  children = [],
  startIndex = 0,
  sideSlides = 1,
  animationStyle = 'default',
  autoPlay,
  arrows = false,
  className
}) => {
  const nextSlideWait = 1000;
  const speed = 3;
  const { length } = children;
  const offsetRadius = clampOffsetRadius(sideSlides, length);

  const [activeIndex, setActiveIndex] = useState(startIndex);
  const [scrollPaused, setScrollPaused] = useState(false);

  const presentableSlides: JSX.Element[] = [];
  for (let i = -offsetRadius; i < 1 + offsetRadius; i++) {
    presentableSlides.push(children[modBySlidesLength(activeIndex + i, length)]);
  }

  const moveSlide = useCallback(
    async (direction: -1 | 1) => {
      setActiveIndex(modBySlidesLength(activeIndex + direction, length));
    },
    [activeIndex, length]
  );

  const animateSlides = useCallback(async () => {
    if (!scrollPaused) {
      await delay(nextSlideWait);
      moveSlide(1);
    }
  }, [scrollPaused, moveSlide]);

  useEffect(() => {
    if (autoPlay) {
      const subscription = interval(speed * 1000).subscribe(() => animateSlides());
      return () => subscription.unsubscribe();
    }
  }, [autoPlay, speed, animateSlides]);

  return (
    <div
      className={classNames(styles.container, arrows && styles.withArrows, className)}
      onMouseOver={() => setScrollPaused(true)}
      onMouseOut={() => setScrollPaused(false)}
      onTouchStart={() => setScrollPaused(true)}
      onTouchEnd={() => setScrollPaused(false)}
      onTouchCancel={() => setScrollPaused(false)}
      onTouchMove={slideTrigger(70, (left) => {
        setScrollPaused(false);
        moveSlide(left ? -1 : 1);
      })}
    >
      {arrows && (
        <Button
          className={styles.button}
          icon={<Arrow />}
          onClick={() => moveSlide(-1)}
          size="small"
          variant="casino"
        />
      )}
      {presentableSlides.map((slide: JSX.Element, presentableIndex: number) => (
        <SpringSlide
          key={slide.key}
          content={slide}
          offsetRadius={clampOffsetRadius(offsetRadius, children.length)}
          index={presentableIndex}
          animationConfig={config[animationStyle]}
        />
      ))}
      {arrows && (
        <Button
          className={classNames(styles.button, styles.right)}
          icon={<Arrow />}
          onClick={() => moveSlide(1)}
          size="small"
          variant="casino"
        />
      )}
    </div>
  );
};

export default Carousel3D;
